import React from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/Breadcrumb';
import { facilities } from '../data/facilities';
import { destinations } from '../data/destinations';
import { MapPin, Building, ShieldCheck, Activity, CheckCircle2, ArrowRight } from 'lucide-react';
export function HospitalDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const facility = facilities.find(f => f.slug === slug);
  if (!facility) {
    return <Navigate to="/not-found" replace data-id="element-921" />;
  }
  const parentDestination = destinations.find(d => d.country === facility.country);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://affordablemedicaltourism.com/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Hospitals',
      item: 'https://affordablemedicaltourism.com/hospitals'
    }, {
      '@type': 'ListItem',
      position: 3,
      name: facility.name,
      item: `https://affordablemedicaltourism.com/hospitals/${facility.slug}`
    }]
  };
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-922">
      <Helmet data-id="element-923">
        <title data-id="element-924">{facility.name} | Affordable Medical Tourism</title>
        <meta name="description" content={`Learn about ${facility.name} in ${facility.city}, ${facility.country}. Discover specialties, accreditations, and plan your medical journey.`} data-id="element-925" />
        <link rel="canonical" href={`https://affordablemedicaltourism.com/hospitals/${facility.slug}`} data-id="element-926" />
        <script type="application/ld+json" data-id="element-927">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <AppHeader data-id="element-928" />

      <main className="flex-1" data-id="element-929">
        {/* Hero */}
        <div className="relative h-[40vh] min-h-[400px] w-full bg-slate-900" data-id="element-930">
          <img src={facility.image} alt={`${facility.name} in ${facility.city}`} className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-931" />
          <div className="absolute inset-0 flex items-center" data-id="element-932">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full" data-id="element-933">
              <Breadcrumb className="mb-6" data-id="element-934">
                <BreadcrumbList data-id="element-935">
                  <BreadcrumbItem data-id="element-936">
                    <BreadcrumbLink href="/" className="text-slate-300 hover:text-white" data-id="element-937">
                      Home
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-938" />
                  <BreadcrumbItem data-id="element-939">
                    <BreadcrumbLink href="/hospitals" className="text-slate-300 hover:text-white" data-id="element-940">
                      Hospitals
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-941" />
                  <BreadcrumbItem data-id="element-942">
                    <BreadcrumbPage className="text-white font-medium" data-id="element-943">
                      {facility.name}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <div className="flex items-center gap-2 text-blue-200 mb-4" data-id="element-944">
                <MapPin className="w-5 h-5" data-id="element-945" />
                <span className="text-lg" data-id="element-946">
                  {facility.city}, {facility.country}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-947">
                {facility.name}
              </h1>
              <div className="flex flex-wrap gap-3" data-id="element-948">
                {facility.accreditations.map(acc => <Badge key={acc} className="bg-white/20 text-white hover:bg-white/30 border-none px-3 py-1" data-id="element-949">
                    <ShieldCheck className="w-4 h-4 mr-2" data-id="element-950" />
                    {acc}
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-951">
          {/* Main */}
          <div className="lg:col-span-2 space-y-12" data-id="element-952">
            <section data-id="element-953">
              <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-954">
                About the Facility
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-955">
                {facility.description}
              </p>
            </section>

            <section data-id="element-956">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-957">
                <Activity className="w-6 h-6 text-blue-600" data-id="element-958" />
                Key Specialties
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-959">
                {facility.keySpecialties.map(spec => <div key={spec} className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-960">
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" data-id="element-961" />
                    <span className="font-medium text-slate-800" data-id="element-962">{spec}</span>
                  </div>)}
              </div>
            </section>

            {parentDestination && <Link to={`/destinations/${parentDestination.slug}`} className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" data-id="element-963">
                Explore medical tourism in {facility.country}
                <ArrowRight className="w-4 h-4" data-id="element-964" />
              </Link>}
          </div>

          {/* Sidebar */}
          <div className="space-y-8" data-id="element-965">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-966">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-967">
                Facility Overview
              </h2>
              <dl className="space-y-6" data-id="element-968">
                <div data-id="element-969">
                  <dt className="text-sm text-slate-500 mb-1 flex items-center gap-2" data-id="element-970">
                    <Building className="w-4 h-4" data-id="element-971" />
                    Total Beds
                  </dt>
                  <dd className="text-lg font-semibold text-slate-900" data-id="element-972">
                    {facility.totalBeds}
                  </dd>
                </div>
                <div className="pt-4 border-t border-slate-200" data-id="element-973">
                  <dt className="text-sm text-slate-500 mb-1 flex items-center gap-2" data-id="element-974">
                    <ShieldCheck className="w-4 h-4" data-id="element-975" />
                    Accreditations
                  </dt>
                  <dd className="text-slate-900 font-medium" data-id="element-976">
                    {facility.accreditations.join(', ')}
                  </dd>
                </div>
                <div className="pt-4 border-t border-slate-200" data-id="element-977">
                  <dt className="text-sm text-slate-500 mb-2" data-id="element-978">
                    Free Online Consultation
                  </dt>
                  <dd data-id="element-979">
                    {facility.freeOnlineConsultation ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-980">
                        Available
                      </Badge> : <Badge variant="secondary" className="bg-slate-200 text-slate-700 hover:bg-slate-200" data-id="element-981">
                        Not Available
                      </Badge>}
                  </dd>
                </div>
              </dl>

              <div className="mt-8 pt-8 border-t border-slate-200" data-id="element-982">
                <Button size="lg" className="w-full" onClick={() => navigate('/contact')} data-id="element-983">
                  Request information
                </Button>
                <p className="text-xs text-slate-500 text-center mt-3" data-id="element-984">
                  Our care team will coordinate directly with {facility.name} on
                  your behalf.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-985" />
    </div>;
}